import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { isDockEmbedded } from '../utils/dockEmbed';
import {
  DEV_PREVIEW_ENABLED,
  PREVIEWABLE_ROLES,
  getDevPreviewRole,
  setDevPreviewRole,
} from '../devRolePreview';

/**
 * DevRolePreviewBar — the control for devRolePreview.js.
 *
 * Renders nothing outside local development. Mounted at the app root, above any
 * role gate, and driven by `realUser` rather than `user` — previewing a role
 * that cannot see this bar must not be able to hide the way back out.
 *
 * Loud on purpose. A silent role override is a trap: you forget it is on, then
 * spend twenty minutes debugging a "bug" that is just the preview doing its job.
 * Hence the permanent amber strip and the always-visible exit.
 */
const DevRolePreviewBar = () => {
  const { realUser } = useAuth();
  const [open, setOpen] = useState(false);

  // Not inside the /acp ToolsDock's tool frames — one bar on the page is enough.
  if (!DEV_PREVIEW_ENABLED || !realUser || isDockEmbedded()) return null;

  const previewRole = getDevPreviewRole();
  const active = !!previewRole && previewRole !== realUser.role;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        left: 12,
        zIndex: 2147483647,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11,
        lineHeight: 1.4,
      }}
    >
      {open && (
        <div
          style={{
            marginBottom: 6,
            maxHeight: '60vh',
            overflowY: 'auto',
            background: '#111',
            color: '#eee',
            border: '1px solid #444',
            borderRadius: 8,
            padding: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,.45)',
            minWidth: 210,
          }}
        >
          <div style={{ padding: '4px 6px 6px', color: '#888' }}>
            render the UI as…
          </div>
          {PREVIEWABLE_ROLES.map((r) => {
            const isReal = r === realUser.role;
            const isCurrent = r === previewRole;
            return (
              <button
                key={r}
                onClick={() => setDevPreviewRole(isCurrent ? null : r)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '4px 6px',
                  borderRadius: 5,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 11,
                  background: isCurrent ? '#b45309' : 'transparent',
                  color: isCurrent ? '#fff' : '#ddd',
                }}
              >
                {r}
                {isReal && <span style={{ color: '#888' }}>  ← you</span>}
              </button>
            );
          })}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: active ? '#b45309' : '#1f1f1f',
          color: active ? '#fff' : '#999',
          border: `1px solid ${active ? '#f59e0b' : '#3a3a3a'}`,
          borderRadius: 999,
          padding: '5px 10px',
          boxShadow: '0 4px 14px rgba(0,0,0,.35)',
        }}
      >
        <button
          onClick={() => setOpen((v) => !v)}
          title={active
            ? `You are ${realUser.email} (${realUser.role}). The UI is rendering as "${previewRole}". `
              + 'Frontend only — API responses are still yours, and capability/featureAccess grants are cleared.'
            : 'Local dev: render the UI as another role'}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            color: 'inherit', font: 'inherit',
          }}
        >
          {active ? `▲ viewing as ${previewRole}` : '▲ view as role'}
        </button>
        {active && (
          <button
            onClick={() => setDevPreviewRole(null)}
            title={`Back to your real role (${realUser.role})`}
            style={{
              background: 'rgba(0,0,0,.28)', border: 'none', borderRadius: 5,
              color: '#fff', cursor: 'pointer', padding: '2px 7px', font: 'inherit',
            }}
          >
            exit
          </button>
        )}
      </div>
    </div>
  );
};

export default DevRolePreviewBar;
